import React, { useEffect, useMemo, useState, lazy, createElement } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, LockIcon, CheckCircleIcon, BrainIcon, AwardIcon, LaptopIcon, DollarSignIcon, GraduationCapIcon, ChurchIcon, RouteIcon, UsersIcon, MedalIcon, ShieldCheckIcon } from 'lucide-react';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import { getUniversityBySlug, getProgramBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { getMetaCookies, resolveSub3 } from '../utils/metaCookies';
import { validateAddress, lookupZip } from '../utils/addressValidation';
import { injectLeadCertificationScripts, readCertificationFields, waitForTrustedFormCert } from '../utils/leadCertificationScripts';
import { fetchClientIp, prefetchClientIp } from '../utils/clientIp';
import { LeadTrackingFields } from '../components/LeadTrackingFields';
import { getUrlPassthroughParams, pruneEmptyParams } from '../utils/urlPassthrough';
// Form value → API value mappings
const educationToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    'high-school': 1,
    'some-high-school': 2,
    ged: 3,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[v] || 9;
};
const startTimeframeToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '4-6-months': 3,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[v] || 6;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};
export function AdvertiserTemplatePage() {
  const {
    collegeSlug,
    programSlug
  } = useParams<{
    collegeSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const initialProgram = university && programSlug ? getProgramBySlug(university, programSlug) : null;
  // Capture optional tracking params from the URL once at mount.
  // sub1/sub2/sub3 + the rest of the documented optional fields can all
  // be passed in via query string and will be forwarded on submit.
  const trackingParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      sub1: q.get('sub1') || '',
      sub2: q.get('sub2') || '',
      sub3: q.get('sub3') || '',
      userIP: q.get('userIP') || '',
      webInitiatingURL: q.get('webInitiatingURL') || document.referrer || '',
      webLandingURL: q.get('webLandingURL') || window.location.href,
      inventoryType: q.get('inventoryType') || '',
      mediaType: q.get('mediaType') || '1'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Inject LeadID + TrustedForm on mount, and never tear them down.
  // TrustedForm builds its certificate by observing the visitor's whole
  // session, so deferring the tag to idle time (or removing it on unmount)
  // shortens or restarts that observation and can leave the certificate
  // field empty at submit time. The IP prefetch stays deferred.
  useEffect(() => {
    injectLeadCertificationScripts();
    const handle = window.setTimeout(prefetchClientIp, 1200);
    return () => window.clearTimeout(handle);
  }, []);
  const metaDescription = university ? initialProgram ? `Explore the ${initialProgram.degreeLevel ? `${initialProgram.degreeLevel} ` : ''}${initialProgram.name} program at ${university.name} and request information about your next steps.` : `Explore online degree programs at ${university.name} and request information about options that align with your educational goals.` : 'The requested university program could not be found. Explore accredited online degree programs on FindOnlineDegrees.';
  useMetaTags({
    title: university ? `${university.name} | Request Information` : 'Program Not Found',
    description: metaDescription,
    ogImage: university?.landingContent?.communityImage?.url,
    ogType: 'website',
    // Partner advertiser pages are paid lead-gen surfaces; keep them out
    // of the index so they don't compete with the brand site for SEO.
    robots: 'noindex, follow'
  });
  // Campus-aware advertisers (e.g. SJVC) use a step 0 to look up the user's
  // campus by zip before showing program options.
  const hasCampusFlow = Boolean(university?.campusZipLookup);
  const firstStep = hasCampusFlow ? 0 : 1;
  const totalSteps = hasCampusFlow ? 5 : 4;
  const [step, setStep] = useState(firstStep);
  const [formData, setFormData] = useState({
    degreeLevel: initialProgram?.degreeLevel || '',
    areaOfStudy: initialProgram?.category || '',
    program: initialProgram?.name || '',
    highestEducation: '',
    startDate: '',
    hsGradYear: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
    consentSms: false,
    campusId: 0,
    campusName: ''
  });
  const [error, setError] = useState('');
  const [campusLookupError, setCampusLookupError] = useState('');
  // True while the ZIP is being verified on the address step.
  const [addressChecking, setAddressChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Auto-advance Step 2
  useEffect(() => {
    if (step === 2 && formData.highestEducation && formData.startDate && formData.hsGradYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, formData.startDate, formData.hsGradYear, step]);
  // ZIP code lookup — shares the cached lookup used by address validation, so
  // verifying on Continue doesn't re-request a ZIP already resolved here.
  useEffect(() => {
    if (formData.zipCode.length !== 5) return;
    let active = true;
    lookupZip(formData.zipCode).then(place => {
      if (!active || !place) return;
      setFormData(prev => ({
        ...prev,
        city: place.city,
        state: place.state
      }));
    });
    return () => {
      active = false;
    };
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-1038" data-mp="2yi1beoddir9w">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center" data-id="element-1039" data-mp="32jw9zl3x6qjz">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1040" data-mp="2okwmigs58hpe">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-1041" data-mp="2tiomzz6utc57">
            We couldn't find the specific program or university you're looking
            for.
          </p>
          <button onClick={() => navigate('/')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-1042" data-mp="2esqu55xzby9o">
            Return Home
          </button>
        </div>
      </div>;
  }
  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = async () => {
    if (step === 0) {
      if (!formData.zipCode || formData.zipCode.length !== 5) {
        setCampusLookupError('Please enter your 5-digit ZIP code to continue.');
        return;
      }
      if (!formData.campusId) {
        setCampusLookupError(`Sorry, that ZIP code isn't served by a ${university.shortName} campus. Please check your ZIP and try again.`);
        return;
      }
      const campusPrograms = university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
      const preselectStillValid = !!initialProgram && campusPrograms.some(p => p.name === initialProgram.name);
      setFormData(prev => ({
        ...prev,
        degreeLevel: preselectStillValid ? initialProgram!.degreeLevel || '' : '',
        areaOfStudy: preselectStillValid ? initialProgram!.category : '',
        program: preselectStillValid ? initialProgram!.name : ''
      }));
      setCampusLookupError('');
      setStep(1);
      return;
    }
    if (step === 1) {
      if (university.singleProgramSelect) {
        if (!formData.program) {
          setError('Please select a program to continue.');
          return;
        }
      } else if (!formData.degreeLevel || !formData.areaOfStudy || !formData.program) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.highestEducation || !formData.startDate || !formData.hsGradYear) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      // Verify the street line looks like an address and that the ZIP exists
      // before the lead is posted. A lookup outage never blocks the user.
      setAddressChecking(true);
      const check = await validateAddress({
        streetAddress: formData.streetAddress,
        zipCode: formData.zipCode,
        state: formData.state
      });
      setAddressChecking(false);
      if (!check.valid) {
        setError(check.message);
        return;
      }
      if (check.place) {
        setFormData(prev => ({
          ...prev,
          city: prev.city || check.place!.city,
          state: prev.state || check.place!.state
        }));
      }
      setStep(4);
    }
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) return formData.phone;
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };
  const handleSubmit = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!university.consentOnSubmit && !formData.consent) {
      setError('Please agree to the consent terms.');
      return;
    }
    if (university.tcpaTextSms && !formData.consentSms) {
      setError('Please check both consent boxes to continue.');
      return;
    }
    setIsSubmitting(true);
    // Read tracking values populated by the LeadID + TrustedForm scripts.
    // Scans every matching field, since TrustedForm appends its own input
    // rather than reusing the placeholder we render, and waits briefly in case
    // the certificate hasn't been issued yet.
    await waitForTrustedFormCert();
    const certification = readCertificationFields();
    const leadIdToken = certification.leadIdToken || generateUUID();
    const trustedFormUrl = certification.trustedFormUrl;
    const trustedFormPingUrl = certification.trustedFormPingUrl;
    // Build the full lead payload per advertiser parameter spec.
    // Includes: required advertiser identifiers, mapped form values,
    // PII, and optional tracking parameters.
    const payload: Record<string, string> = {
      // Everything the landing URL carried, forwarded verbatim. Listed first
      // so the resolved values below always take precedence.
      ...getUrlPassthroughParams(location.search),
      // Required advertiser identifiers. URL params (mPubGroupID, or
      // mPubID + mPubCampaignID) override the per-university config values
      // when present; otherwise fall back to the university config.
      ...getPublisherParams(location.search, {
        mPubID: university.mPubID || '',
        mPubCampaignID: university.mPubCampaignID || ''
      }),
      mBrandID: university.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: `s-${generateUUID()}`,
      leadIdToken,
      // Mapped form values. When the partner provides a numeric program
      // catalog (programId), post that ID as mProgram; otherwise fall back
      // to the program name.
      degreeInterest: formData.degreeLevel,
      areaStudy: formData.areaOfStudy,
      mProgram: (() => {
        const matched = programsForThisFlow.find(p => p.name === formData.program);
        if (matched?.mProgramValue) return matched.mProgramValue;
        return matched?.programId ? String(matched.programId) : formData.program;
      })(),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      gradYear: formData.hsGradYear,
      // PII
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      campusLocation: formData.campusId ? String(formData.campusId) : '',
      smsConsent: formData.consentSms ? '1' : '0',
      // Optional tracking
      ipAddress: await fetchClientIp(),
      useragent: navigator.userAgent,
      sub1: trackingParams.sub1,
      sub2: trackingParams.sub2,
      // sub3 carries the Meta match keys ("<fbc>_<fbp>") for the Conversions
      // API when the Pixel cookies are present.
      sub3: resolveSub3(window.location.search) || trackingParams.sub3,
      userIP: trackingParams.userIP,
      webInitiatingURL: trackingParams.webInitiatingURL,
      webLandingURL: trackingParams.webLandingURL,
      trustedFormUrl,
      trustedFormPingUrl,
      inventoryType: trackingParams.inventoryType,
      deviceType: getDeviceType(),
      mediaType: trackingParams.mediaType
    };
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Advertiser Lead] payload:', payload);
    // Report the submission to GTM once (and only once) the lead has been
    // sent. `leadIdToken` doubles as the dedupe id for Meta CAPI.
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'university-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    // Host-and-post: the advertiser provides a posting endpoint per partner.
    // Configure it via university.leadPostUrl. If unset, no post is fired
    // (the payload is still logged above for QA / verification).
    const postBase = university.leadPostUrl || '';
    if (postBase) {
      const qs = pruneEmptyParams(new URLSearchParams(payload)).toString();
      const fullUrl = `${postBase}?${qs}`;
      console.log('[Advertiser Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Advertiser Lead] post error:', err);
      }
    } else {
      console.log('[Advertiser Lead] No leadPostUrl configured for this advertiser — skipping post.');
    }
    // The conversion is reported through GTM (dataLayer `form_submission`)
    // once the lead post is confirmed — no Meta Pixel event is fired here.
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Advertisers without a configured posting endpoint never produce a
      // pixel load, so confirm on the success state instead.
      reportSubmission();
      // Partners with a dedicated thank-you page keep the user there; the
      // fallback card is transient, so it bounces home.
      if (!university.thankYou) {
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    }, 1000);
  };
  // Derived program list — honors campus-aware advertisers (filters to the
  // programs available at the user's matched campus).
  const programsForThisFlow = hasCampusFlow && formData.campusId && university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
  // Derived options for Step 1
  const uniqueAreas = Array.from(new Set(programsForThisFlow.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel).map(p => p.category)));
  const availablePrograms = programsForThisFlow.filter(p => (!formData.degreeLevel || p.degreeLevel === formData.degreeLevel) && (!formData.areaOfStudy || p.category === formData.areaOfStudy));
  const currentYear = new Date().getFullYear();
  // Years run back to 1990; everything earlier is collapsed into the
  // "1989 or earlier" option, which posts 1989.
  const years = Array.from({
    length: currentYear - 1989
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  if (isSuccess) {
    // Partners with thank-you copy get a branded confirmation page that
    // stays put; everyone else keeps the brief redirect card.
    if (university.thankYou) {
      const ty = university.thankYou;
      return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-1043" data-mp="1l5g4ofk3wdo6">
          <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-1044" data-mp="1p9e28lts9gf4">
            <div className="max-w-7xl mx-auto flex items-center justify-center" data-id="element-1045" data-mp="2wmo930ur5tsc">
              {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-1046" data-mp="30m6sfs6xdwt2" /> : <span className="text-xl font-bold" style={{
              color: university.primaryColor
            }} data-id="element-1047" data-mp="l0x83mshfcn7">
                  {university.name}
                </span>}
            </div>
          </header>

          <main className="flex-grow" data-id="element-1048" data-mp="6ag7u6rl2srw">
            <section className="px-4 py-16 md:py-24" style={{
            backgroundColor: university.primaryColor
          }} data-id="element-1049" data-mp="67aicqf1lovn">
              <div className="max-w-2xl mx-auto text-center text-white" data-id="element-1050" data-mp="3tfe7c50jaci4">
                <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-6" data-id="element-1051" data-mp="3ofjhm4shvsyn">
                  <CheckCircleIcon className="w-9 h-9 text-white" data-id="element-1052" data-mp="1rn9jp5q2i1d7" />
                </div>
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mb-4" data-id="element-1053" data-mp="1mndgh8p72dyp">
                  {formData.firstName ? `${ty.heading}, ${formData.firstName}` : ty.heading}
                </h1>
                <p className="text-base md:text-lg text-white/85 leading-relaxed" data-id="element-1054" data-mp="2azwsny8a504u">
                  {ty.body}
                </p>
              </div>
            </section>

            {ty.nextSteps && ty.nextSteps.length > 0 && <section className="bg-white px-4 py-14 md:py-20" data-id="element-1055" data-mp="217qzil6akco2">
                <div className="max-w-3xl mx-auto" data-id="element-1056" data-mp="1wc1z6fmg6rqp">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-1057" data-mp="1s8drlwxtfw5g">
                    What happens next
                  </h2>
                  <ol className="space-y-6" data-id="element-1058" data-mp="1na2endc855j0">
                    {ty.nextSteps.map((s, i) => <li key={s} className="flex items-start gap-4" data-id="element-1059" data-mp="1ick4xkqdum9y" data-mpi={i}>
                        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{
                    backgroundColor: `${university.ctaColor}2e`,
                    color: university.primaryColor
                  }} data-id="element-1060" data-mp="3hms8jv2leeqe">
                          {i + 1}
                        </span>
                        <p className="text-base text-gray-700 leading-relaxed pt-1" data-id="element-1061" data-mp="3u0f9tgqjy2fd">
                          {s}
                        </p>
                      </li>)}
                  </ol>
                  <div className="mt-10 text-center" data-id="element-1062" data-mp="368ic0tjxtax9">
                    <button onClick={() => navigate('/')} className="px-7 py-3.5 font-bold text-sm uppercase tracking-wider rounded-md transition-colors" style={{
                  backgroundColor: university.ctaColor,
                  color: university.ctaTextColor || '#ffffff'
                }} data-id="element-1063" data-mp="3690e46ta6jd3">
                      Explore More Schools
                    </button>
                  </div>
                </div>
              </section>}
          </main>

          <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-1064" data-mp="2mlaaoepe58g4">
            <div className="max-w-5xl mx-auto space-y-2" data-id="element-1065" data-mp="30kryoruv4hof">
              <p data-id="element-1066" data-mp="30i5rwufsq679">
                © {new Date().getFullYear()} {university.name}. All rights
                reserved.
              </p>
              <TrackingNoticeLinks data-id="element-1067" data-mp="28skly2ih9pfb" />
            </div>
          </footer>
        </div>;
    }
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-1068" data-mp="23usl2izuusyz">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center" data-id="element-1069" data-mp="2hlxnfsulxzd0">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1070" data-mp="2cufnn84bm4tm">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1071" data-mp="1p3sbgandgt19" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1072" data-mp="1l4b4tdw7lpui">
            Thanks, {formData.firstName}!
          </h2>
          <p className="text-gray-600" data-id="element-1073" data-mp="1ayt3rn8025v9">
            {university.name} will reach out shortly. Redirecting you to the
            homepage...
          </p>
        </div>
      </div>;
  }
  // Dynamic hero title — show the selected program once chosen, otherwise a generic prompt.
  // On the first step (entry view), prefer the partner's high-CTR landing headline if provided.
  const isEntryStep = step === firstStep;
  // Once the user has chosen both a program and a degree level, surface the
  // degree in the H1 (e.g. "Bachelor's in Early Childhood Education").
  const selectedProgramTitle = formData.program && formData.degreeLevel ? `${formData.degreeLevel} in ${formData.program}` : formData.program || initialProgram?.name;
  const heroTitle = isEntryStep && university.landingContent ? university.landingContent.headline : selectedProgramTitle || `Find Your ${university.shortName} Program`;
  // Show the fuller landing subheadline on every step (entry and beyond) so
  // the program-selection / later steps aren't left with just the short
  // mission line. Falls back to the mission when no landing copy exists.
  const heroDescription = university.landingContent?.subheadline !== undefined ? university.landingContent.subheadline : university.mission;
  const featureIconMap: Record<string, any> = {
    BrainIcon,
    AwardIcon,
    LaptopIcon,
    DollarSignIcon,
    GraduationCapIcon,
    ChurchIcon,
    RouteIcon,
    UsersIcon,
    MedalIcon,
    ShieldCheckIcon
  };
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-1074" data-mp="12jn8rqscp4sz">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <LeadTrackingFields data-id="element-1075" data-mp="xjr6px37rng2" />

      {/* White top nav bar with centered logo */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-1076" data-mp="3na4ifcetukaw">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-1077" data-mp="3ib1ddryuke2x">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-1078" data-mp="1xts7mahubmm4" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-1079" data-mp="27pvhk2ixopjm">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-1080" data-mp="2hgghbtacg044">
            <LockIcon className="w-3.5 h-3.5" data-id="element-1081" data-mp="2lk61m6jzjvjc" />
            Secure
          </div>
        </div>
      </header>

      {/* Branded main wrapper — neutral surface for the supporting sections */}
      <main className="flex-grow bg-white" data-id="element-1082" data-mp="2veeremhmtadt">
        {/* HERO — full-bleed brand color, optionally overlaid with a partner image */}
        <section className="relative py-12 md:py-20 px-3 sm:px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage && !university.heroImageFlip ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-1083" data-mp="11288f1ihxpay">
          {/* Mirrored hero art: rendered as an element so it can be flipped,
           moving the subjects clear of the form card on the right. */}
          {university.heroBackgroundImage && university.heroImageFlip && <div aria-hidden="true" className="absolute inset-0 overflow-hidden" data-id="element-1084" data-mp="17zkpgvhql6t5">
              <img src={university.heroBackgroundImage} alt="" className="w-full h-full object-cover scale-x-[-1]" data-id="element-1085" data-mp="1lp4qnwvo5w0c" />
            </div>}

          {/* Opt-in scrim for hero photos that are too bright for white copy.
           Partners whose hero art is already dark leave it off. */}
          {university.heroBackgroundImage && university.heroImageScrim && <div aria-hidden="true" className="absolute inset-0" style={{
          backgroundColor: university.primaryColor,
          opacity: 0.8
        }} data-id="element-1086" data-mp="2a986lybugxz1" />}

          {/* Form column is given the larger share: it's the reason the page
           exists, and the extra measure keeps the TCPA text readable. */}
          <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-start" data-id="element-1087" data-mp="1qmaf3f8g9jmf">
            {/* Left: Branded hero copy — on mobile the whole column sits
             BELOW the form, so the first question is the first thing on the
             page and the school name reads as the answer to "who is this?" */}
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-1088" data-mp="17ohc6cxtt9p1">
              <p className="text-sm lg:text-base font-semibold mb-3 lg:mb-4 text-white/90" data-id="element-1089" data-mp="17p985ovc59fu">
                {university.name}
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-1090" data-mp="1s5bpr5hn3e6u">
                {heroTitle}
              </h1>
              {heroDescription && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-1091" data-mp="1j4ncid02lhv0">
                  {heroDescription}
                </p>}
              {university.landingContent?.heroHighlights && university.landingContent.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-1092" data-mp="14g9ag4ah9xfi">
                    {university.landingContent.heroHighlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-1093" data-mp="10f7b7ngk9ldj">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-1094" data-mp="qghrdp53tqun" />
                        <span data-id="element-1095" data-mp="lgvdreld68lb">{h}</span>
                      </li>)}
                  </ul>}
            </div>

            {/* Right: Form card — appears directly under the H1 on mobile
             (above the fold), to the right on desktop. */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-5 sm:p-6 md:p-8 lg:p-10 order-1 lg:order-2" data-id="element-1096" data-mp="lbnpx8j6vw18">
              {/* Progress Indicator */}
              <div className="mb-6" data-id="element-1097" data-mp="q8edegxbptwo">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center" data-id="element-1098" data-mp="3osmmuj7qbex4">
                  {university.formHeading || 'Take the next step in your education.'}
                </h2>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-1099" data-mp="2kd3p4oes7fo">
                  <div className="h-full transition-all duration-300" style={{
                  width: `${(step - firstStep + 1) / totalSteps * 100}%`,
                  backgroundColor: university.ctaColor
                }} data-id="element-1100" data-mp="7bcx3zybk7df" />
                </div>
              </div>

              {/* Step 0: Campus lookup by zip (campus-aware advertisers only) */}
              {step === 0 && hasCampusFlow && <div className="space-y-6" data-id="element-1101" data-mp="2o7artxmzwyb9">
                  <div data-id="element-1102" data-mp="29cw6hqfylctf">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1103" data-mp="29bl4nw62qf4y">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setCampusLookupError('');
                  // Auto-resolve campus when the zip becomes valid.
                  // If no match, leave the dropdown alone so the
                  // user can still pick manually.
                  if (val.length === 5 && university.campusZipLookup) {
                    const match = university.campusZipLookup(val);
                    if (match) {
                      setFormData(prev => ({
                        ...prev,
                        zipCode: val,
                        campusId: match.id,
                        campusName: match.name
                      }));
                      return;
                    }
                  }
                  updateField('zipCode', val);
                }} placeholder="e.g. 93291" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-lg" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1104" data-mp="20jszo0ztjy7r" />
                  </div>

                  {/* Campus readout is only meaningful for partners with more
                   than one campus. Online-only schools (CCU) just need the
                   ZIP, so a one-option disabled dropdown is noise. */}
                  {(university.landingContent?.campusList?.length || 0) > 1 && <div data-id="element-1105" data-mp="1b4k9uyzzrana">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1106" data-mp="1g1u8n599syns">
                        Campus
                      </label>
                      <select value={formData.campusId || ''} disabled aria-readonly="true" tabIndex={-1} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700 appearance-none cursor-not-allowed" style={selectStyle as any} data-id="element-1107" data-mp="1kz400qg7zke9">
                        <option value="" data-id="element-1108" data-mp="3vujg941e1bns">
                          -- Enter ZIP to match a campus --
                        </option>
                        {(university.landingContent?.campusList || []).map(c => <option key={c.id} value={c.id} data-id="element-1109" data-mp="3ekfkm6fmydda">
                              {c.name} Campus
                            </option>)}
                      </select>
                      <p className="mt-2 text-xs text-gray-500" data-id="element-1110" data-mp="b1w1btexqhum">
                        Your campus is automatically determined by your ZIP
                        code.
                      </p>
                    </div>}

                  {campusLookupError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1111" data-mp="3t7qwk9fqbzdx">
                      <p className="text-red-600 text-sm" data-id="element-1112" data-mp="2lvry67j5hhy5">
                        {campusLookupError}
                      </p>
                    </div>}
                </div>}

              {/* Step 1: Program Selection — single dropdown variant */}
              {step === 1 && university.singleProgramSelect && <div className="space-y-6" data-id="element-1113" data-mp="3vrnallml42ig">
                  <div data-id="element-1114" data-mp="3vsolnq3lop06">
                    <label htmlFor="program-select" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1115" data-mp="3vtgjz8ol10u5">
                      Program of Interest
                    </label>
                    <select id="program-select" value={formData.program} onChange={e => {
                  const selected = programsForThisFlow.find(p => p.name === e.target.value);
                  updateField('program', e.target.value);
                  updateField('degreeLevel', selected?.degreeLevel || '');
                  updateField('areaOfStudy', selected?.category || '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1116" data-mp="xajdj3zssjjr">
                      <option value="" data-id="element-1117" data-mp="2cth9ubuf92iv">-- Select Program --</option>
                      {programsForThisFlow.map(p => <option key={p.name} value={p.name} data-id="element-1118" data-mp="2rin73u9p8io4">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 1: Program Selection */}
              {step === 1 && !university.singleProgramSelect && <div className="space-y-6" data-id="element-1119" data-mp="2jqljs2gxpp44">
                  <div data-id="element-1120" data-mp="2jptxxjd535r1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1121" data-mp="2joiszg7yjryc">
                      Degree of Interest
                    </label>
                    <select value={formData.degreeLevel} onChange={e => {
                  updateField('degreeLevel', e.target.value);
                  updateField('areaOfStudy', '');
                  updateField('program', '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1122" data-mp="2sinerl88id70">
                      <option value="" data-id="element-1123" data-mp="1ejc663zxie47">-- Select Degree of Interest --</option>
                      {university.degreeLevels.map(level => <option key={level} value={level} data-id="element-1124" data-mp="19lafaww9hai6">
                          {level}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1125" data-mp="zto4h1nu5dfy">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1126" data-mp="zu5zjuyrhlma">
                      Area of Study
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', e.target.value);
                  updateField('program', '');
                }} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1127" data-mp="qt80zs73e6b3">
                      <option value="" data-id="element-1128" data-mp="1fcqpo6zt39zd">-- Select Area of Study --</option>
                      {uniqueAreas.map(area => <option key={area} value={area} data-id="element-1129" data-mp="11c68ff9jhm7z">
                          {area}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-1130" data-mp="senjhzsrynqt">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1131" data-mp="sffdx7amyffi">
                      Program
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', e.target.value)} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1132" data-mp="2mqu5ltn7ioxw">
                      <option value="" data-id="element-1133" data-mp="tv19ynxcnmsp">-- Select Program --</option>
                      {availablePrograms.map(p => <option key={p.name} value={p.name} data-id="element-1134" data-mp="17thfwcne199s">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 2: Academic Background */}
              {step === 2 && <div className="space-y-6" data-id="element-1135" data-mp="34odymd1pvcve">
                  <div data-id="element-1136" data-mp="1ks1uv5ijluh2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1137" data-mp="1ktms52w8rrim">
                      Highest Level of Education
                    </label>
                    <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1138" data-mp="1tu1co01dt2l2">
                      <option value="" data-id="element-1139" data-mp="2mg2sq2mppojk">-- Please Select --</option>
                      <option value="high-school" data-id="element-1140" data-mp="2hkf4bpg20zf4">
                        High School Diploma/GED
                      </option>
                      <option value="some-college" data-id="element-1141" data-mp="312tasfonnvru">Some College</option>
                      <option value="associates" data-id="element-1142" data-mp="363gwclu47keo">Associate's Degree</option>
                      <option value="bachelors" data-id="element-1143" data-mp="2r7huhsxlmghz">Bachelor's Degree</option>
                      <option value="some-high-school" data-id="element-1144" data-mp="2w5j9a1nzrtnn">Some High School</option>
                    </select>
                  </div>

                  <div data-id="element-1145" data-mp="2idd3n9igt52g">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1146" data-mp="2id3dnlxf6xwr">
                      When would you like to start classes?
                    </label>
                    <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1147" data-mp="28h9w1o4uge84">
                      <option value="" data-id="element-1148" data-mp="9dc53nbw0wb">-- Please Select --</option>
                      <option value="immediately" data-id="element-1149" data-mp="3rfes9qm5hkot">Immediately</option>
                      <option value="1-3-months" data-id="element-1150" data-mp="3rd1zeieutpwz">1-3 months</option>
                      <option value="3-6-months" data-id="element-1151" data-mp="swr0wegvhq5c">3-6 months</option>
                      <option value="6-12-months" data-id="element-1152" data-mp="nxwktkaaifox">6-12 months</option>
                      <option value="1-year-plus" data-id="element-1153" data-mp="iz35dmbg0em4">1+ year</option>
                      <option value="undecided" data-id="element-1154" data-mp="ixidy5kfdbsh">Undecided</option>
                    </select>
                  </div>

                  <div data-id="element-1155" data-mp="ezkkurnja5jt">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1156" data-mp="33p2c5sgr4hn5">
                      High School Graduation Year
                    </label>
                    <select value={formData.hsGradYear} onChange={e => updateField('hsGradYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1157" data-mp="2zmfh7iosaglq">
                      <option value="" data-id="element-1158" data-mp="26wm349o6y220">-- Please Select --</option>
                      {years.map(year => <option key={year} value={year} data-id="element-1159" data-mp="2buvx4gy6iwm1">
                          {year}
                        </option>)}
                      <option value="1989" data-id="element-1160" data-mp="cl7e5v3086nb">1989 or earlier</option>
                    </select>
                  </div>
                </div>}

              {/* Step 3: Location */}
              {step === 3 && <div className="space-y-6" data-id="element-1161" data-mp="3dek8n3wxuh4i">
                  {formData.city && formData.state ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-1162" data-mp="3sbur4x800n9q">
                      <span className="text-green-600 text-xl" data-id="element-1163" data-mp="3nd9q4vkx9zk8">📍</span>
                      <div className="flex-1" data-id="element-1164" data-mp="2zme289jl7oi9">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-1165" data-mp="2zn5m4n3tnxsp">
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                  updateField('city', '');
                  updateField('state', '');
                  updateField('zipCode', '');
                }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-1166" data-mp="39drz09k5ma7p">
                        change
                      </button>
                    </div> : <div data-id="element-1167" data-mp="w7k8robjy4if">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1168" data-mp="hhcxptnvjnrr">
                        ZIP Code
                      </label>
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('zipCode', val);
                }} placeholder="e.g. 90210" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1169" data-mp="mg7dsnugiy86" />
                    </div>}

                  {formData.city && formData.state && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-1170" data-mp="1ho9pqwe3c30t">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1171" data-mp="1doh9rymdaulf">
                        Street Address
                      </label>
                      <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1172" data-mp="13pa0pnmo1m44" />
                    </div>}
                </div>}

              {/* Step 4: Contact & Submit */}
              {step === 4 && <div className="space-y-6" data-id="element-1173" data-mp="2in4e3cv307oj">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1174" data-mp="2iofm5ovk81lg">
                    <div data-id="element-1175" data-mp="2mozf0g93oj1s">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1176" data-mp="2mo7xuag92nv1">
                        First Name
                      </label>
                      <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-1177" data-mp="2wmnflmte0krv" />
                    </div>
                    <div data-id="element-1178" data-mp="11xh6dhsngb2l">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1179" data-mp="11x7a5ch0h7og">
                        Last Name
                      </label>
                      <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-1180" data-mp="1bnsb36k5qoaj" />
                    </div>
                  </div>

                  <div data-id="element-1181" data-mp="1t6sd0vxnyrx1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1182" data-mp="1t7bvcdr87ibl">
                      Email Address
                    </label>
                    <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1183" data-mp="1x61947g6f1vr" />
                  </div>

                  <div data-id="element-1184" data-mp="38m9d8db9al9j">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1185" data-mp="38mhpc2zk1mjo">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-1186" data-mp="k5lu3r0cepmg" />
                  </div>

                  <div className="pt-4 space-y-3" data-id="element-1187" data-mp="1buypwysxaf6w">
                    {university.tcpaTextSms && <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1188" data-mp="39pfdyhqr49dl">
                        <input type="checkbox" checked={formData.consentSms} onChange={e => updateField('consentSms', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-1189" data-mp="366q1ps2tjeao" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1190" data-mp="3g3ba8ne1r7j9">
                          {university.tcpaTextSms}
                        </span>
                      </label>}
                    {university.consentOnSubmit ?
                // Consent is given by clicking the submit button, so the
                // disclosure is shown as plain text. Jornaya's
                // "no interaction" pattern: a hidden input carrying the
                // disclosure id, inside a label wrapping the text.
                <label className="block text-xs text-gray-600 leading-relaxed p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-1191" data-mp="264jy07ihq81j">
                        <input type="hidden" id="leadid_tcpa_disclosure" data-id="element-1192" data-mp="1w6vqegzsd61y" />
                        {university.tcpaText}
                      </label> : <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-1193" data-mp="1idnzkrjj4elb">
                        {/* Jornaya's checkbox disclosure pattern. */}
                        <input id="leadid_tcpa_disclosure" type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-1194" data-mp="1dg4mhuyki7uy" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-1195" data-mp="ptit9d32k5mn">
                          {university.tcpaText || `By checking this box, I consent to receive calls and text messages from ${university.name} at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.`}
                        </span>
                      </label>}
                  </div>
                </div>}

              {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1196" data-mp="1hlrl23nxs9sw">
                  <p className="text-red-600 text-sm" data-id="element-1197" data-mp="3bzm22o7k38lz">{error}</p>
                </div>}

              <div className="mt-8" data-id="element-1198" data-mp="3gyqb9euhd6cb">
                {step < 4 ? <button onClick={handleContinue} disabled={addressChecking} className="w-full py-4 font-bold rounded-lg text-lg transition-colors shadow-md hover:shadow-lg disabled:cursor-wait disabled:opacity-70" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-1199" data-mp="3lqqb77ajvz8w">
                    {addressChecking ? 'Checking your address…' : 'Continue'}
                  </button> : <button onClick={handleSubmit} disabled={isSubmitting || !university.consentOnSubmit && !formData.consent || Boolean(university.tcpaTextSms) && !formData.consentSms} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-1200" data-mp="hcrwrz5z556o">
                    {isSubmitting ? 'Submitting...' : university.submitLabel || 'Submit Application'}
                  </button>}

                {/* Back sits beneath the primary action: it's the secondary
                 path, and keeping it here removes the gap that opened up
                 between the heading and the first question. */}
                {step > firstStep && <button onClick={() => {
                setStep(step - 1);
                setError('');
              }} className="mt-4 mx-auto flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors" data-id="element-1201" data-mp="1u43eh5ukxrpi">
                    <ArrowLeftIcon className="w-4 h-4" data-id="element-1202" data-mp="3emdz72vq3ptb" />
                    <span className="text-sm font-medium" data-id="element-1203" data-mp="3imepplw3jrgq">Back</span>
                  </button>}

                {university.formFootnote && <p className="mt-4 text-sm text-gray-600 leading-relaxed text-center" data-id="element-1204" data-mp="a7nxr8bm86cf">
                    {university.formFootnote}
                  </p>}

                {/* Paid-placement disclosure, kept quiet so it reads as a
                 label rather than competing with the form's content. */}
                <p className="mt-4 text-right text-[11px] text-gray-400" data-id="element-1205" data-mp="6743cciffgtz">
                  Sponsored Ad
                </p>
              </div>
            </div>
          </div>
        </section>
        {/* /HERO */}

        {/* Supporting site content — renders on every step so the
            program page (deep link) reads as a full marketing site,
            not just a form. */}
        {university.landingContent && <>
            {/* Stats strip */}
            {university.landingContent.stats && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-1206" data-mp="2nd2s5nmsr6yc">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-1207" data-mp="1znqhskfqgsug">
                  {university.landingContent.stats.map(s => <div key={s.label} className="text-center" data-id="element-1208" data-mp="1umu63kp542eh">
                      <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
                color: university.primaryColor
              }} data-id="element-1209" data-mp="29euv7fprg5n6">
                        {s.value}
                      </div>
                      <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-1210" data-mp="1bxmdsbymur2x">
                        {s.label}
                      </div>
                    </div>)}
                </div>
              </section>}

            {/* Why us — feature grid */}
            {/* Why-this-school: an editorial statement on the left carries the
             argument, with the supporting points as a scannable divided list
             on the right — so one idea leads and the rest support it. */}
            <section className="bg-white py-16 md:py-24 px-4" data-id="element-1211" data-mp="1ecs0wtspuchd">
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-1212" data-mp="1eb8lt6isq7h6">
                <div data-id="element-1213" data-mp="1j5uu30tkzjx1">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{
                color: university.primaryColor
              }} data-id="element-1214" data-mp="1o2lcvvwrstm5">
                    About {university.name}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight" data-id="element-1215" data-mp="1x42mcvfo9x1v">
                    Education built around your career goals.
                  </h2>
                  <div className="w-12 h-1 rounded-full my-6" style={{
                backgroundColor: university.ctaColor
              }} data-id="element-1216" data-mp="3dj73jsl38i2w" />
                  <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-1217" data-mp="33mc67d0jwnq7">
                    {university.mission}
                  </p>
                  <button onClick={() => window.scrollTo({
                top: 0,
                behavior: 'smooth'
              })} className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 font-bold text-sm uppercase tracking-wider rounded-md transition-colors" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-1218" data-mp="53dm9bixn2ks">
                    Get Started
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1219" data-mp="10qwvb6dwe5lk" />
                  </button>
                </div>

                <ul className="divide-y divide-gray-200 border-y border-gray-200" data-id="element-1220" data-mp="vsd6my7x6fpi">
                  {university.landingContent.features.map(f => {
                const Icon = featureIconMap[f.icon] || GraduationCapIcon;
                return <li key={f.title}
                // Single-line points center against their icon; points
                // with a description align to the top instead.
                className={`flex gap-4 py-5 ${f.description ? 'items-start' : 'items-center'}`} data-id="element-1221" data-mp="mt8cs57azajo">
                        <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                    backgroundColor: `${university.ctaColor}1f`
                  }} data-id="element-1222" data-mp="15qt21ddzcb4t">
                          <Icon className="w-5 h-5" style={{
                      color: university.primaryColor
                    }} data-id="element-1223" data-mp="3g4qj1d567x74" />
                        </span>
                        <div data-id="element-1224" data-mp="3p4lznxhhwkej">
                          <h3 className="text-base font-semibold text-gray-900" data-id="element-1225" data-mp="3p3c35cqajka0">
                            {f.title}
                          </h3>
                          {f.description && <p className="text-sm text-gray-600 leading-relaxed mt-1" data-id="element-1226" data-mp="2x5m3gledwdn">
                              {f.description}
                            </p>}
                        </div>
                      </li>;
              })}
                </ul>
              </div>
            </section>

            {/* Info sections — alternating layout */}
            {university.landingContent.infoSections && university.landingContent.infoSections.map((s, i) => <section key={s.heading} className={`py-16 md:py-24 px-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-1227" data-mp="qkx32xhvuxl9" data-mpi={i}>
                  <div className="max-w-5xl mx-auto" data-id="element-1228" data-mp="2ht4jx1s2ewga">
                    {s.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
              color: university.ctaColor
            }} data-id="element-1229" data-mp="2whsa0sj56dyu">
                        {s.eyebrow}
                      </p>}
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5 max-w-3xl" data-id="element-1230" data-mp="31hn4t761fnp6">
                      {s.heading}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1231" data-mp="3agrxvxytyoty">
                      {s.body}
                    </p>
                    {s.bullets && <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl" data-id="element-1232" data-mp="1g3sdlrji96gc">
                        {s.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-700" data-id="element-1233" data-mp="jjdwkzclhqn">
                            <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                  color: university.ctaColor
                }} data-id="element-1234" data-mp="3hxnfbr7ydrfo" />
                            <span className="text-sm md:text-base" data-id="element-1235" data-mp="3dz7kx29jlgs8">{b}</span>
                          </li>)}
                      </ul>}
                  </div>
                </section>)}

            {/* Community image — partner-supplied lifestyle photo */}
            {university.landingContent.communityImage && <section className="bg-white py-16 md:py-24 px-4" data-id="element-1236" data-mp="127aldtek92mx">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-1237" data-mp="2yb8brnue144j">
                  <div className="order-2 md:order-1" data-id="element-1238" data-mp="2u9n4ci0mhi1q">
                    {university.landingContent.communityImage.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-1239" data-mp="2p98vwyv40636">
                        {university.landingContent.communityImage.eyebrow}
                      </p>}
                    {university.landingContent.communityImage.heading && <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-1240" data-mp="21lhi1qtx7kzs">
                        {university.landingContent.communityImage.heading}
                      </h2>}
                    {university.landingContent.communityImage.body && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-1241" data-mp="1hjd5i1z9ymb6">
                        {university.landingContent.communityImage.body}
                      </p>}
                  </div>
                  <div className="order-1 md:order-2 flex justify-center" data-id="element-1242" data-mp="1rilz2tq89yx0">
                    <img src={university.landingContent.communityImage.url} alt={university.landingContent.communityImage.alt || university.name} loading="lazy" decoding="async" className="w-full max-w-md object-contain" data-id="element-1243" data-mp="1wgx66wp51vbj" />
                  </div>
                </div>
              </section>}

            {/* Testimonial */}
            {university.landingContent.testimonial && <section className="py-16 md:py-24 px-4" style={{
          backgroundColor: `${university.primaryColor}08`
        }} data-id="element-1244" data-mp="l0p0wnibo5w6">
                <div className="max-w-3xl mx-auto text-center" data-id="element-1245" data-mp="81zhs8nx3gst">
                  <div className="text-6xl leading-none mb-4 font-serif" style={{
              color: university.primaryColor
            }} data-id="element-1246" data-mp="7zlmbypbs2kq">
                    "
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed mb-6" data-id="element-1247" data-mp="3q7uaagxbuk8f">
                    {university.landingContent.testimonial.quote}
                  </blockquote>
                  <div className="text-sm text-gray-600" data-id="element-1248" data-mp="41fhiw81rx2g">
                    <span className="font-bold text-gray-900" data-id="element-1249" data-mp="3v5mb60fy9gor">
                      {university.landingContent.testimonial.name}
                    </span>
                    {' — '}
                    {university.landingContent.testimonial.program}
                  </div>
                </div>
              </section>}

            {/* Career section with image */}
            {university.landingContent.careerSection && <section className="bg-white py-16 md:py-24 px-4" data-id="element-1250" data-mp="1n03o9ucocpn5">
                <div className="max-w-6xl mx-auto rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-1251" data-mp="18zj5uzao8vu6">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-1252" data-mp="1d1e8c74c9f9j">
                    <div className="p-8 md:p-12 flex flex-col justify-center md:order-2" data-id="element-1253" data-mp="20pnl6jjkqsoj">
                      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-1254" data-mp="25pjp6is9uk43">
                        {university.landingContent.careerSection.headline}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-1255" data-mp="1vyfazlaf73ab">
                        {university.landingContent.careerSection.body}
                      </p>
                      <button onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} className="self-start px-7 py-3 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg" style={{
                  backgroundColor: university.ctaColor
                }} data-id="element-1256" data-mp="2og1iopkxhjqi">
                        Get Started
                      </button>
                    </div>
                    <div className="min-h-[280px] md:min-h-0 md:order-1" data-id="element-1257" data-mp="2dpy743dqgqui">
                      <img src={university.landingContent.careerSection.imageUrl} alt={university.landingContent.careerSection.imageAlt || university.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-1258" data-mp="28p0mlk593kpu" />
                    </div>
                  </div>
                </div>
              </section>}

            {/* Final CTA band */}
            {university.landingContent.finalCta && <section className="py-16 md:py-20 px-4" style={{
          backgroundColor: university.primaryColor
        }} data-id="element-1259" data-mp="3dvigp65xoofu">
                <div className="max-w-4xl mx-auto text-center text-white" data-id="element-1260" data-mp="3nm3e4q9vfmx6">
                  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-1261" data-mp="3rn6rn64g56v8">
                    {university.landingContent.finalCta.headline}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1262" data-mp="5hjj603bcv08">
                    {university.landingContent.finalCta.body}
                  </p>
                  <button onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl" style={{
              backgroundColor: university.ctaColor,
              color: '#fff'
            }} data-id="element-1263" data-mp="2784utkz5ph96">
                    {university.landingContent.finalCta.buttonLabel || 'Request Information'}
                  </button>
                </div>
              </section>}
          </>}

        {/* Branded footer */}
        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-1264" data-mp="21ouku4iklkoc">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-1265" data-mp="26no124p8rps7">
            <p data-id="element-1266" data-mp="q71tyv7f3mn9">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-1267" data-mp="v7zal3cs48mn">
              {university.accreditation} • Founded {university.founded}
            </p>
            <div className="max-w-2xl mx-auto pt-2" data-id="element-1268" data-mp="39g06zjxsc0jt">
              <TrackingNoticeLinks data-id="element-1269" data-mp="34eaxw9u4hwyd" />
            </div>
          </div>
        </footer>
      </main>
    </div>;
}